import { createTheme, rem, type MantineThemeOverride } from "@mantine/core";
import { graphite, primary, secondary, accent } from "../colors";
import { brandColorTokens } from "./brandColorTokens";
import { createBaseResolver, composeResolvers } from "./resolver";
import { invertedFilledVariantResolver } from "./variants";
import type { CanucciSemanticTokens } from "./tokens";

type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[] ? U[] : T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export const baseBreakpoints = {
  xs: "30em",
  sm: "48em",
  md: "72em",
  lg: "82em",
  xl: "92em",
} as const;

const baseSpacing = {
  xxs: rem(4),
  xs: rem(8),
  sm: rem(12),
  md: rem(16),
  lg: rem(20),
  xl: rem(24),
  "2xl": rem(40),
  "3xl": rem(48),
  "4xl": rem(64),
};

const baseOther: CanucciSemanticTokens = {
  textShadowOnDark: "0 0 24px rgba(0, 0, 0, 0.3)",
  spacing: {
    pageGap: baseSpacing.xl,
    sectionGap: baseSpacing.lg,
    mobileHeaderHeight: "3.5rem",
    desktopHeaderHeight: "2.25rem",
    paginationDotSize: "0.625rem",
    buttonIconSize: baseSpacing.lg,
    starPadding: "0.15rem",
    smallButtonPadding: baseSpacing.xxs,
  },
  sizes: {
    maxPageWidthDesktop: "80rem",
    maxNarrowPageWidthDesktop: "64rem",
    maxPageWidthMobile: "100%",
    modalWidth: "28rem",
    contentWidth: "42rem",
    narrowContent: "30rem",
  },
  sizesInPixels: {
    maxPageWidthDesktop: 1280,
    maxNarrowPageWidthDesktop: 1024,
  },
  semanticColors: {
    text: "#1F2122",
    invertedText: "#FFFFFF",
    invertedHoverBackground: "rgba(255, 255, 255, 0.25)",
    accent: "#9B4DA3",
    accentSubtle: "#E8D4E9",
    borderSubtle: "#E0E3E7",
    borderMedium: "var(--mantine-color-gray-3)",
    borderStrong: "#CDD2D8",
    error: "var(--mantine-color-red-4)",
    borderError: "var(--mantine-color-red-4)",
    authBackground: brandColorTokens.authShellBackground,
    infoSoft: brandColorTokens.infoSoftBackground,
    textSecondary: "#5B646F",
    textMuted: "#838C98",
    fgOnDarkDim: brandColorTokens.fgOnDarkDim,
    authSurfaceHover: brandColorTokens.authShellHover,
  },
  zIndex: {
    background: 5,
    backgroundOverlay: 6,
    content: 10,
    sticky: 15,
    overlay: 200,
    modal: 201,
    fab: 1000,
  },
};

const baseTheme: MantineThemeOverride = {
  variantColorResolver: invertedFilledVariantResolver,
  // Body font. Override in app theme to change. When changing, update the <link> in the app's index.html too.
  fontFamily: "Instrument Sans, sans-serif",
  headings: {
    // Heading font is intentionally separate from fontFamily so apps can override one without affecting the other.
    // e.g. set headings.fontFamily: "Manrope, sans-serif" in collect-ui while keeping body as Instrument Sans.
    fontFamily: "Instrument Sans, sans-serif",
    fontWeight: "900",
    sizes: {
      h1: { fontSize: rem(22), lineHeight: "1.625rem" },
      h2: { fontSize: rem(20), lineHeight: "1.5rem" },
      h3: { fontSize: rem(18), lineHeight: "1.5rem" },
      h4: { fontSize: rem(16), lineHeight: "1.25rem" },
      h5: { fontSize: rem(14), lineHeight: "1.125rem" },
      h6: { fontSize: rem(12), lineHeight: "1rem" },
    },
  },
  colors: {
    // "primary" is kept as graphite so all existing apps are visually unchanged.
    // Brand palette entries are registered here so apps can use color="brand" / color="secondary" / color="accent"
    // without any additional registration. Apps override primaryColor to "brand" at design-system rollout time.
    primary: graphite,
    brand: primary, // ocean teal (L0 ocean ramp)
    secondary, // gold CTA (L0 gold ramp, base #FFD472)
    accent, // ocean600-aligned mid teal
  },
  breakpoints: baseBreakpoints,
  primaryColor: "primary",
  // Pick black/white label text from background luminance (e.g. gold secondary → dark text).
  autoContrast: true,
  defaultRadius: "md",
  fontSizes: {
    xs: rem(12),
    sm: rem(14),
    md: rem(16),
    lg: rem(18),
    xl: rem(20),
    "2xl": rem(22),
    "3xl": rem(24),
    "4xl": rem(28),
    "5xl": rem(32),
  },
  lineHeights: {
    xs: "1.05",
    sm: "1.25",
    md: "1.25",
    lg: "1.5",
    xl: "1.5",
  },
  spacing: baseSpacing,
  shadows: {
    xs: "0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)",
    sm: "0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)",
    md: "0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.05)",
    lg: "0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)",
    xl: "0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)",
  },
  other: baseOther,
};

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function deepMerge<T>(target: T, source: DeepPartial<T>): T {
  if (!isObject(target) || !isObject(source)) {
    return source as T;
  }

  const output = { ...target } as Record<string, unknown>;
  for (const key of Object.keys(source)) {
    const sourceValue = (source as Record<string, unknown>)[key];
    if (sourceValue === undefined) continue;

    const targetValue = (target as Record<string, unknown>)[key];
    if (isObject(targetValue) && isObject(sourceValue)) {
      output[key] = deepMerge(targetValue, sourceValue);
    } else {
      output[key] = sourceValue;
    }
  }

  return output as T;
}

export function createBaseTheme(overrides: DeepPartial<MantineThemeOverride> = {}) {
  return createTheme(deepMerge(baseTheme, overrides));
}

export { brandColorTokens, type BrandColorTokenKey } from "./brandColorTokens";
export { collectConversationSemanticColors } from "./collectSemanticColors";
export { defaultBrandColors, defaultBrandFontFamily } from "./defaultBrand";
export {
  canucciSurfaceDecorationCircles,
  canucciThemeIdentityColorAtIndex,
  canucciThemeIdentityColors,
} from "./slideSurfaceDecorations";
export * from "./darkSurfaceUi";
export * from "./paperSurface";
export { composeResolvers, createBaseResolver, invertedFilledVariantResolver };
export type { CanucciSemanticTokens } from "./tokens";

export * from "./typography";
export * from "./brandControls";
export * from "./brandFields";
export * from "./facilitatorInteract";

export * from "./productChrome";

export * from "./productDialog";
